/**
 * 认证服务
 * 职责：封装认证相关的API请求，包括登录、注册、用户信息等
 *
 * 统一调用规范：
 * - 所有接口通过 publish 后端代理调用，由后端通过 SDK 访问 shared 服务
 * - 统一使用 api.js (axios实例)，不再直接调用 shared-service
 * - 保证调用链路：前端 -> publish后端 -> auth-sdk -> shared服务
 *
 * API 路径规范：
 * - 认证代理接口：/api/pubs/v1/proxy/auth/*
 * - 路径后缀对应 shared 服务的接口路径，方便映射
 *
 * 依赖：api.js (axios实例)
 */
import api from './api';

const authService = {
  // ==================== 登录注册相关 ====================

  /**
   * 用户登录
   * 代理路径：/api/pubs/v1/proxy/auth/auth/login -> /api/shrd/v1/auth/login
   */
  login: (credentials) => {
    return api.post('/api/pubs/v1/proxy/auth/auth/login', credentials);
  },

  /**
   * 用户注册
   * 代理路径：/api/pubs/v1/proxy/auth/auth/register -> /api/shrd/v1/auth/register
   */
  register: (userData) => {
    return api.post('/api/pubs/v1/proxy/auth/auth/register', userData);
  },

  /**
   * 发送短信验证码（包含风险控制和人机验证）
   * 代理路径：/api/pubs/v1/proxy/auth/auth/send-code -> /api/shrd/v1/auth/send-code
   */
  sendVerificationCode: (phone, purpose, captchaToken) => {
    return api.post('/api/pubs/v1/proxy/auth/auth/send-code', {
      phone,
      purpose,
      captchaToken
    });
  },

  /**
   * 验证短信验证码
   * 代理路径：/api/pubs/v1/proxy/auth/auth/verify-code -> /api/shrd/v1/auth/verify-code
   */
  verifyCode: (phone, code, purpose) => {
    return api.post('/api/pubs/v1/proxy/auth/auth/verify-code', {
      phone,
      code,
      purpose
    });
  },

  /**
   * 重置密码
   * 代理路径：/api/pubs/v1/proxy/auth/auth/reset-password -> /api/shrd/v1/auth/reset-password
   */
  resetPassword: (data) => {
    return api.post('/api/pubs/v1/proxy/auth/auth/reset-password', data);
  },

  // ==================== 用户信息相关 ====================

  /**
   * 获取当前用户信息
   * 代理路径：/api/pubs/v1/proxy/auth/user/current -> /api/shrd/v1/user/current
   */
  getUserInfo: () => {
    return api.get('/api/pubs/v1/proxy/auth/user/current');
  },

  /**
   * 修改密码
   * 代理路径：/api/pubs/v1/proxy/auth/user/change-password -> /api/shrd/v1/user/change-password
   */
  changePassword: (oldPassword, newPassword) => {
    return api.post('/api/pubs/v1/proxy/auth/user/change-password', null, {
      params: {
        oldPassword,
        newPassword
      }
    });
  },

  // ==================== Token 管理 ====================

  /**
   * 刷新token
   * 代理路径：/api/pubs/v1/proxy/auth/auth/refresh-token -> /api/shrd/v1/auth/refresh-token
   */
  refreshToken: () => {
    return api.post('/api/pubs/v1/proxy/auth/auth/refresh-token');
  },

  /**
   * 用户登出
   * 代理路径：/api/pubs/v1/proxy/auth/auth/logout -> /api/shrd/v1/auth/logout
   */
  logout: () => {
    return api.post('/api/pubs/v1/proxy/auth/auth/logout');
  },

  // ==================== 微信登录相关 ====================

  /**
   * 获取微信授权URL
   * 代理路径：/api/pubs/v1/proxy/auth/auth/wechat/authorize-url -> /api/shrd/v1/auth/wechat/authorize-url
   */
  getWechatAuthorizeUrl: (redirectUri, redirect = '/') => {
    return api.get('/api/pubs/v1/proxy/auth/auth/wechat/authorize-url', {
      params: { redirectUri, redirect }
    });
  },

  /**
   * 微信登录回调
   * 代理路径：/api/pubs/v1/proxy/auth/auth/wechat/callback -> /api/shrd/v1/auth/wechat/callback
   */
  wechatCallback: (code, state) => {
    return api.get('/api/pubs/v1/proxy/auth/auth/wechat/callback', {
      params: { code, state }
    });
  },

  /**
   * 微信绑定手机号
   * 代理路径：/api/pubs/v1/proxy/auth/auth/wechat/bind-phone -> /api/shrd/v1/auth/wechat/bind-phone
   */
  wechatBindPhone: (payload) => {
    return api.post('/api/pubs/v1/proxy/auth/auth/wechat/bind-phone', payload);
  },

  // ==================== 验证码相关 ====================

  /**
   * 获取验证码配置
   * 代理路径：/api/pubs/v1/proxy/auth/captcha/config -> /api/shrd/v1/captcha/config
   */
  getCaptchaConfig: () => {
    return api.get('/api/pubs/v1/proxy/auth/captcha/config');
  },

  // ==================== 业务相关（使用 publish 后端接口） ====================

  /**
   * 印签管理：列表
   * GET /api/pubs/v1/seals
   */
  listUserSeals: (sealType) => {
    return api.get('/api/pubs/v1/seals', { params: { sealType } });
  },

  /**
   * 印签管理：生成企业公章
   * POST /api/pubs/v1/seals/generate-enterprise
   */
  generateEnterpriseSeal: (enterpriseId, sealName) => {
    return api.post('/api/pubs/v1/seals/generate-enterprise', { enterpriseId, sealName });
  },

  /**
   * 印签管理：生成个人手写签名
   * POST /api/pubs/v1/seals/generate-personal
   */
  generatePersonalSignature: (payload) => {
    return api.post('/api/pubs/v1/seals/generate-personal', payload);
  },

  /**
   * 获取企业认证列表
   * 代理路径：/api/pubs/v1/proxy/auth/verification/enterprise/list
   */
  getEnterpriseVerificationList: () => {
    return api.get('/api/pubs/v1/proxy/auth/verification/enterprise/list');
  },

  /**
   * 印签管理：删除
   * DELETE /api/pubs/v1/seals/{id}
   */
  deleteUserSeal: (id) => {
    return api.delete(`/api/pubs/v1/seals/${id}`);
  },
};

// 导出单独的函数供组件使用
export const {
  login,
  register,
  sendVerificationCode,
  verifyCode,
  resetPassword,
  getUserInfo,
  changePassword,
  refreshToken,
  logout,
  getWechatAuthorizeUrl,
  wechatCallback,
  wechatBindPhone,
  getCaptchaConfig,
  listUserSeals,
  generateEnterpriseSeal,
  generatePersonalSignature,
  getEnterpriseVerificationList,
  deleteUserSeal,
} = authService;

// 导出默认服务
export default authService;
